import {
  Button,
  DuplicateIcon,
  EmptyState,
  ExclamationmarkIcon,
  Grid,
  GridRow,
  LazyLoader,
  Modal,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { FC, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { copyTextToClipboard } from '../../../utils/copyTextToClipboard';
import {
  extractRawConstraintEntries,
  type ParsedTimefoldConstraintRow,
  parseTimefoldScoreAnalysis,
} from '../../../utils/timetable/parseTimefoldScoreAnalysis';
import { cleanConstraintLabel } from '../../../utils/timetable/timefoldConstraints';
import { OmniChip } from '../../Solver/OmniChip';
import { useTimetableStore } from '../TimetableProvider';
import { CopyRawAnalysisButton } from './CopyRawAnalysisButton';
import styles from './TimefoldAnalyzeModal.module.scss';

const MAX_DETAIL_LINES = 80;

export const TimefoldAnalyzeModal: FC = observer(() => {
  const { t } = useTranslation();
  const pinboardStore = useTimetableStore();
  const modal = pinboardStore.timefoldAnalyzeModal;
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [copiedRowId, setCopiedRowId] = useState<string | null>(null);
  const copiedResetTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (copiedResetTimer.current !== null) window.clearTimeout(copiedResetTimer.current);
    },
    [],
  );

  // Kopiert nur die Roh-Einträge dieser einen Regel (alle Quellformate der ScoreAnalysis)
  const copyConstraintRaw = async (row: ParsedTimefoldConstraintRow) => {
    const extracted = extractRawConstraintEntries(modal.result, row.label) ?? {
      constraint: row.label,
      scores: { hard: row.hardScore, medium: row.mediumScore, soft: row.softScore },
      detailSections: row.detailSections,
    };
    await copyTextToClipboard(JSON.stringify(extracted, null, 2));
    setCopiedRowId(row.id);
    if (copiedResetTimer.current !== null) window.clearTimeout(copiedResetTimer.current);
    copiedResetTimer.current = window.setTimeout(() => setCopiedRowId(null), 2000);
  };

  const parsed = useMemo(() => {
    if (pinboardStore.timefoldParsedAnalysis) {
      return pinboardStore.timefoldParsedAnalysis;
    }
    if (modal.result == null) return null;
    return parseTimefoldScoreAnalysis(modal.result);
  }, [modal.result, pinboardStore.timefoldParsedAnalysis]);

  const jsonFormatted = useMemo(() => {
    if (modal.result == null) return '';
    try {
      return JSON.stringify(modal.result, null, 2);
    } catch {
      return String(modal.result);
    }
  }, [modal.result]);

  const handleClose = () => {
    void pinboardStore.setTimefoldAnalyzeModal({
      isOpen: false,
    });
  };

  return (
    <Modal
      className={styles.modal}
      isOpen={modal.isOpen}
      onRequestClose={handleClose}
      title={t('pinboard.timefoldAnalyze.title')}
      actions={
        // Die Kopfzeile des Modals zieht ihr erstes Element auf volle Breite – der Wrapper hält den Chip klein.
        <span className={styles['header-chip']}>
          <OmniChip />
        </span>
      }
      parent={document.fullscreenElement ? document.getElementById('timetable-grid') : null}
    >
      <Grid>
        {modal.isLoading ? (
          <EmptyState
            title={t('pinboard.timefoldAnalyze.loadingHint')}
            icon={<LazyLoader size='xxl' embedded />}
            iconColor='var(--color-grey-dark)'
            forcedHeight='50vh'
          />
        ) : (
          <>
            {modal.partialClassScope && (
              <div className={styles.scopeHint}>
                <ExclamationmarkIcon />
                <div>{t('pinboard.timefoldAnalyze.partialClassScopeHint')}</div>
              </div>
            )}
            {modal.error ? (
              <div className={styles.error}>{modal.error}</div>
            ) : modal.result != null ? (
              <>
                {parsed ? (
                  <GridRow spacingBottom='m' direction='column'>
                    <div className={styles.totals}>
                      <div className={styles.totalItem}>
                        <strong>{t('pinboard.timefoldAnalyze.totalScore')}</strong> {parsed.scoreText}
                      </div>
                      <div className={styles.totalItem}>
                        <strong>{t('pinboard.timefoldAnalyze.hardTotal')}</strong> {parsed.hardTotal}
                      </div>
                      <div className={styles.totalItem}>
                        <strong>{t('pinboard.timefoldAnalyze.mediumTotal')}</strong> {parsed.mediumTotal}
                      </div>
                      <div className={styles.totalItem}>
                        <strong>{t('pinboard.timefoldAnalyze.softTotal')}</strong> {parsed.softTotal}
                      </div>
                    </div>
                    {parsed.constraints.length > 0 ? (
                      <div className={styles.tableWrap}>
                        <table className={styles.table}>
                          <thead>
                            <tr>
                              <th className={styles.constraintCol}>{t('pinboard.timefoldAnalyze.colConstraint')}</th>
                              <th className={styles.num}>{t('pinboard.timefoldAnalyze.colHard')}</th>
                              <th className={styles.num}>{t('pinboard.timefoldAnalyze.colMedium')}</th>
                              <th className={styles.num}>{t('pinboard.timefoldAnalyze.colSoft')}</th>
                              <th className={styles.num}>{t('pinboard.timefoldAnalyze.colMatches')}</th>
                              <th className={styles.actionsCol} />
                            </tr>
                          </thead>
                          <tbody>
                            {parsed.constraints.map((row) => {
                              const hasDetails = row.detailSections.some((s) => s.lines.length > 0);
                              const isOpen = expandedId === row.id;
                              const rowTitle = row.localized?.title ?? cleanConstraintLabel(row.label);
                              return (
                                <Fragment key={row.id}>
                                  <tr>
                                    <td className={styles.constraintCol}>
                                      {hasDetails ? (
                                        <Button
                                          className={styles.expandBtn}
                                          hierarchy='tertiary'
                                          onClick={() => setExpandedId(isOpen ? null : row.id)}
                                          type='button'
                                        >
                                          {isOpen ? '▼' : '▶'} {rowTitle}
                                        </Button>
                                      ) : (
                                        rowTitle
                                      )}
                                    </td>
                                    <td className={styles.num}>{row.hardScore}</td>
                                    <td className={styles.num}>{row.mediumScore}</td>
                                    <td className={styles.num}>{row.softScore}</td>
                                    <td className={styles.num}>{row.matchCount === null ? '—' : row.matchCount}</td>
                                    <td className={styles.actionsCol}>
                                      <Button
                                        ariaLabel={t('pinboard.timefoldAnalyze.copyConstraint')}
                                        className={styles.copyBtn}
                                        hierarchy='ghost'
                                        icon={copiedRowId === row.id ? undefined : <DuplicateIcon className='small' />}
                                        onClick={() => void copyConstraintRaw(row)}
                                        type='button'
                                      >
                                        {copiedRowId === row.id ? '✓' : undefined}
                                      </Button>
                                    </td>
                                  </tr>
                                  {isOpen &&
                                    hasDetails &&
                                    row.detailSections.map((sec, secIdx) => {
                                      const shown = sec.lines.slice(0, MAX_DETAIL_LINES);
                                      const hidden = sec.lines.length - shown.length;
                                      return (
                                        <tr className={styles.detailBlock} key={`${row.id}-s-${String(secIdx)}`}>
                                          <td colSpan={6}>
                                            {sec.matchScoreText ? (
                                              <div className={styles.matchScore}>
                                                {t('pinboard.timefoldAnalyze.matchScoreLabel')}: {sec.matchScoreText}
                                              </div>
                                            ) : null}
                                            <ul className={styles.detailList}>
                                              {shown.map((line, li) => (
                                                <li key={`${String(li)}-${String(line.length)}`}>{line}</li>
                                              ))}
                                            </ul>
                                            {hidden > 0 ? (
                                              <p className={styles.moreLines}>
                                                {t('pinboard.timefoldAnalyze.moreLinesHidden', { count: hidden })}
                                              </p>
                                            ) : null}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                </Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <EmptyState
                        size='small'
                        subtitle={t('pinboard.timefoldAnalyze.noBreakdown')}
                        icon={<ExclamationmarkIcon />}
                        iconColor='var(--color-grey-dark)'
                      />
                    )}
                    <p className={styles.interpretHint}>{t('pinboard.timefoldAnalyze.interpretationHint')}</p>
                  </GridRow>
                ) : (
                  <EmptyState
                    size='small'
                    subtitle={t('pinboard.timefoldAnalyze.noParsedSummary')}
                    icon={<ExclamationmarkIcon />}
                    iconColor='var(--color-grey-dark)'
                  />
                )}
                <GridRow spacingTop='m' direction='column'>
                  <div className={styles.rawActions}>
                    <CopyRawAnalysisButton />
                  </div>
                  <details className={styles.rawDetails}>
                    <summary className={styles.rawSummary}>{t('pinboard.timefoldAnalyze.rawJsonTitle')}</summary>
                    <p className={styles.rawHint}>{t('pinboard.timefoldAnalyze.rawJsonHint')}</p>
                    <pre className={styles.jsonPre}>{jsonFormatted}</pre>
                  </details>
                </GridRow>
              </>
            ) : (
              <EmptyState
                size='small'
                subtitle={t('pinboard.timefoldAnalyze.noData')}
                icon={<ExclamationmarkIcon />}
                iconColor='var(--color-grey-dark)'
              />
            )}
          </>
        )}
      </Grid>
    </Modal>
  );
});

TimefoldAnalyzeModal.displayName = 'TimefoldAnalyzeModal';
